
import React from 'react';
import { DataTable } from "@/components/ui/data-table";
import { 
  Users, RefreshCw, Edit, Mail, Phone, Calendar, 
  CheckCircle, XCircle, ShieldCheck, UserCheck, Clock, Trash2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { UserRole } from "@/types/roles";
import { Customer } from '@/hooks/useCustomers';
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface CustomerTableProps {
  customers: Customer[];
  loading: boolean;
  onEditUser: (customer: Customer) => void;
  onApproveUser?: (customer: Customer) => void;
  onDeleteUser?: (customer: Customer) => void;
}

export const CustomerTable: React.FC<CustomerTableProps> = ({ 
  customers, 
  loading, 
  onEditUser,
  onApproveUser,
  onDeleteUser
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-6 w-6 animate-spin text-gray-400 mr-2" />
        <span className="text-gray-300">Daten werden geladen...</span>
      </div>
    );
  }
  
  if (!customers || customers.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <Users className="h-12 w-12 mx-auto mb-3 opacity-20" />
        <p>Keine Benutzer gefunden</p>
      </div>
    );
  }
  
  console.log("Rendering customer table with", customers.length, "customers:", customers);
  
  const columns = [
    {
      header: "Name",
      accessorKey: "name",
      cell: ({ row }: { row: { original: Customer } }) => <span className="text-white">{row.original.name || 'Unnamed'}</span>
    },
    {
      header: "Email",
      accessorKey: "email",
      cell: ({ row }: { row: { original: Customer } }) => (
        <div className="flex items-center">
          <Mail className="h-4 w-4 mr-2 text-gray-400" />
          <span className="text-gray-300">{row.original.email}</span>
        </div>
      )
    },
    {
      header: "Telefon",
      accessorKey: "phone",
      cell: ({ row }: { row: { original: Customer } }) => (
        row.original.phone ? (
          <div className="flex items-center">
            <Phone className="h-4 w-4 mr-2 text-gray-400" />
            <span className="text-gray-300">{row.original.phone}</span>
          </div>
        ) : (
          <span className="text-gray-500">-</span>
        )
      )
    },
    {
      header: "Status",
      accessorKey: "status",
      cell: ({ row }: { row: { original: Customer } }) => {
        if (row.original.role === 'pending') {
          return (
            <span className="px-2 py-1 bg-orange-900/30 text-orange-400 rounded-full text-xs flex items-center w-fit">
              <Clock className="h-3 w-3 mr-1" />
              Eingeladen
            </span>
          );
        }
        
        return row.original.is_approved ? (
          <span className="px-2 py-1 bg-green-900/30 text-green-400 rounded-full text-xs">
            Aktiv
          </span>
        ) : (
          <span className="px-2 py-1 bg-gray-800 text-gray-400 rounded-full text-xs">
            Inaktiv
          </span>
        );
      }
    },
    {
      header: "Freigabe",
      accessorKey: "is_approved",
      cell: ({ row }: { row: { original: Customer } }) => {
        if (row.original.role === 'pending' || !row.original.is_approved) {
          return (
            <Button 
              variant="outline" 
              size="sm" 
              className="bg-amber-900/20 text-amber-400 border-amber-800 hover:bg-amber-800 hover:text-amber-200"
              onClick={() => {
                console.log("Approve button clicked for:", row.original);
                if (onApproveUser) {
                  onApproveUser(row.original);
                }
              }}
            >
              <UserCheck className="h-3 w-3 mr-1" />
              Aktivieren
            </Button>
          );
        }
        
        return (
          <span className="px-2 py-1 bg-green-900/30 text-green-400 rounded-full text-xs flex items-center w-fit">
            <CheckCircle className="h-3 w-3 mr-1" />
            Freigegeben
          </span>
        );
      }
    },
    {
      header: "Rolle",
      accessorKey: "role",
      cell: ({ row }: { row: { original: Customer } }) => {
        console.log(`Displaying role for ${row.original.email}: ${row.original.role}`);
        
        if (row.original.role === 'admin') {
          return (
            <div className="flex items-center">
              <ShieldCheck className="h-4 w-4 mr-1 text-blue-400" />
              <span className="text-blue-400">Admin</span>
            </div>
          );
        } 
        
        if (row.original.role === 'pending') {
          return (
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-1 text-orange-400" />
              <span className="text-orange-400">Einladung</span>
            </div>
          );
        }
        
        return (
          <div className="flex items-center">
            <Users className="h-4 w-4 mr-1 text-gray-400" />
            <span className="text-gray-300">Benutzer</span>
          </div>
        );
      }
    },
    {
      header: "Meta",
      accessorKey: "meta_connected",
      cell: ({ row }: { row: { original: Customer } }) => (
        row.original.meta_connected ? (
          <CheckCircle className="h-5 w-5 text-green-500" />
        ) : (
          <XCircle className="h-5 w-5 text-gray-500" />
        )
      )
    },
    {
      header: "Registriert",
      accessorKey: "registered_at",
      cell: ({ row }: { row: { original: Customer } }) => (
        <div className="flex items-center">
          <Calendar className="h-4 w-4 mr-2 text-gray-500" />
          <span className="text-gray-400">
            {row.original.registered_at ? new Date(row.original.registered_at).toLocaleDateString() : '-'}
          </span>
        </div>
      )
    },
    {
      header: "Aktionen",
      accessorKey: "actions",
      cell: ({ row }: { row: { original: Customer } }) => (
        <div className="flex justify-end gap-1">
          <Button 
            variant="ghost" 
            size="icon" 
            className="text-blue-400 hover:text-blue-300 hover:bg-blue-950"
            onClick={() => {
              console.log("Edit button clicked for:", row.original);
              onEditUser(row.original);
            }}
            title="Benutzer bearbeiten"
          >
            <Edit className="h-4 w-4" />
          </Button>
          
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-red-400 hover:text-red-300 hover:bg-red-950"
                title="Benutzer löschen"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-gray-800 border-gray-700 text-white">
              <AlertDialogHeader>
                <AlertDialogTitle>Benutzer löschen</AlertDialogTitle>
                <AlertDialogDescription className="text-gray-300">
                  Sind Sie sicher, dass Sie den Benutzer "{row.original.name}" ({row.original.email}) löschen möchten? 
                  Diese Aktion kann nicht rückgängig gemacht werden.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-gray-700 hover:bg-gray-600 text-white border-gray-600">Abbrechen</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={() => {
                    console.log("Delete button confirmed for:", row.original);
                    if (onDeleteUser) {
                      onDeleteUser(row.original);
                    }
                  }}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Löschen
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )
    }
  ];
  
  return <DataTable data={customers} columns={columns} />;
};
